import { create } from 'zustand';
import type { User } from '@guanwei/shared';
import { api, getToken, setToken } from './api';

interface AuthApiResp {
  user: User;
  token: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  /** 是否已尝试过会话恢复（用于首次渲染避免闪烁） */
  initialized: boolean;
  hydrate: () => Promise<void>;
  loginWithPassword: (email: string, password: string) => Promise<void>;
  register: (email: string, code: string, password: string) => Promise<void>;
  setPassword: (email: string, code: string, password: string) => Promise<void>;
  resetPassword: (email: string, code: string, password: string) => Promise<void>;
  sendCode: (email: string, purpose: 'REGISTER' | 'SET_PASSWORD' | 'RESET_PASSWORD') => Promise<void>;
  logout: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  token: null,
  loading: false,
  initialized: false,

  async hydrate() {
    if (get().initialized) return;
    const token = getToken();
    if (!token) {
      set({ initialized: true });
      return;
    }
    set({ token, loading: true });
    try {
      const data = await api.get<{ user: User }>('/auth/me');
      set({ user: data.user, loading: false, initialized: true });
    } catch {
      setToken(null);
      set({ user: null, token: null, loading: false, initialized: true });
    }
  },

  async sendCode(email, purpose) {
    await api.post('/auth/send-code', { email, purpose }, { auth: false });
  },

  async loginWithPassword(email, password) {
    set({ loading: true });
    try {
      const data = await api.post<AuthApiResp>(
        '/auth/login-password',
        { email, password },
        { auth: false },
      );
      setToken(data.token);
      set({ user: data.user, token: data.token, loading: false, initialized: true });
    } catch (err) {
      set({ loading: false });
      throw err;
    }
  },

  async register(email, code, password) {
    set({ loading: true });
    try {
      const data = await api.post<AuthApiResp>(
        '/auth/register',
        { email, code, password },
        { auth: false },
      );
      setToken(data.token);
      set({ user: data.user, token: data.token, loading: false, initialized: true });
    } catch (err) {
      set({ loading: false });
      throw err;
    }
  },

  async setPassword(email, code, password) {
    set({ loading: true });
    try {
      const data = await api.post<AuthApiResp>(
        '/auth/set-password',
        { email, code, password },
        { auth: false },
      );
      setToken(data.token);
      set({ user: data.user, token: data.token, loading: false, initialized: true });
    } catch (err) {
      set({ loading: false });
      throw err;
    }
  },

  async resetPassword(email, code, password) {
    set({ loading: true });
    try {
      const data = await api.post<AuthApiResp>(
        '/auth/reset-password',
        { email, code, password },
        { auth: false },
      );
      setToken(data.token);
      set({ user: data.user, token: data.token, loading: false, initialized: true });
    } catch (err) {
      set({ loading: false });
      throw err;
    }
  },

  async logout() {
    try {
      await api.post('/auth/logout', undefined);
    } catch {
      /* 忽略登出失败，本地清空即可 */
    }
    setToken(null);
    set({ user: null, token: null });
  },
}));
